import { SkyText, SkyTooltip, type SkyDataGridColumn } from "@skypointcloud/skypoint-ui";
import type { DataflowLastRun, DataflowSummary } from "@shared/types/dataflows";
import { useConnectionRowActivity } from "@/components/connections/ConnectionStateBadge";
import { formatCount, formatDuration } from "@/components/runs/runFormatting";
import { useElapsedSeconds } from "@/hooks/use-elapsed-seconds";
import { isActivityLive } from "@/lib/runs/runActivity";

/**
 * The connections list's Rows and Run time columns (Session 10, B17).
 *
 * Both read the `lastRun` projection `GET /dataflows` already carries, so the list fetches
 * nothing per row. The two numbers mean different things and the cells say so:
 *
 * - **Rows** is THIS connection's share of its last run: rows extracted, and rows loaded once a
 *   load reported them (the same rule as `RunTotals`).
 * - **Run time** is the WHOLE run's duration. A schedule run covers several connections and has
 *   no per-connection time on the list, so the cell carries that caveat as a tooltip that opens
 *   on hover and on keyboard focus, and as hidden text for a screen reader in browse mode.
 *
 * A number nobody reported reads as a dash, never as zero.
 */

export const RUN_TIME_HINT = "The whole run, which may cover other connections";

/**
 * The two labelled lines of a row's Rows cell (#972): `Extracted: 1,000` and `Loaded: 996`. Null
 * until the run has a table for this connection. The loaded line waits for a load that reported.
 */
export function connectionRowsLines(run: DataflowLastRun | undefined): string[] | null {
  if (run?.rowsExtracted == null) return null;
  // Zero extracted while the load read rows means only the load reported the tables: the
  // extract count is unknown, not zero (the `rowsLineParts` short rule, and the Runs view's dash).
  const extractUnreported = run.rowsExtracted === 0 && (run.rowsLoaded ?? 0) > 0;
  const lines = [`Extracted: ${extractUnreported ? "unknown" : formatCount(run.rowsExtracted)}`];
  if (run.rowsLoaded !== null) lines.push(`Loaded: ${formatCount(run.rowsLoaded)}`);
  return lines;
}

export function ConnectionRowsCell({ dataflow }: { dataflow: DataflowSummary }) {
  const lines = connectionRowsLines(dataflow.lastRun);
  const testId = `connection-rows-${dataflow.displayName}`;
  if (!lines) {
    return (
      <SkyText size={200} font="numeric" color="muted" testId={testId}>
        —
      </SkyText>
    );
  }
  return (
    // One line each, never split between label and number, left-aligned in the cell so the
    // labels line up from row to row.
    <span className="flex flex-col items-start text-left" data-testid={testId}>
      {lines.map((line) => (
        <SkyText key={line} size={200} font="numeric" className="whitespace-nowrap">
          {line}
        </SkyText>
      ))}
    </span>
  );
}

/**
 * The Run time cell. #2094: it follows the row's import as the Status and Last import cells do
 * (`useConnectionRowActivity`): once the tab's feed says the run ended, the "Running for" timer
 * stops at once, even while the list's own read still says running, and the cell reads a dash
 * until the list brings the run's duration. A finished outcome never sits beside a running timer.
 */
export function ConnectionRunTimeCell({ dataflow }: { dataflow: DataflowSummary }) {
  const { showsLive, activity } = useConnectionRowActivity(dataflow);
  const endedLive = showsLive && !isActivityLive(activity);
  const seconds = endedLive
    ? (activity.durationSeconds ?? null)
    : (dataflow.lastRun?.durationSeconds ?? null);
  const testId = `connection-run-time-${dataflow.displayName}`;
  // #1201: a run still going reads how long it has been going, never a bare dash.
  const elapsed = useElapsedSeconds(
    seconds === null && !endedLive && dataflow.lastRun?.status === "running"
      ? dataflow.lastRun.startedAt
      : null,
  );
  if (seconds === null && elapsed !== null) {
    return (
      // Two lines that each never wrap, so "Running for 48h 00m" fits the 78 px column (#1434).
      <SkyText size={200} font="numeric" testId={testId}>
        <span className="block whitespace-nowrap">Running for</span>{" "}
        <span className="block whitespace-nowrap">{formatDuration(elapsed)}</span>
      </SkyText>
    );
  }
  if (seconds === null) {
    return (
      <SkyText size={200} font="numeric" color="muted" testId={testId}>
        —
      </SkyText>
    );
  }
  return (
    // The trigger takes focus (the run page's precedent, runs/detail.tsx) so a keyboard user can
    // open the caveat too; a hover-only tooltip would hide it from everyone without a mouse.
    // The value holds the caveat in `sr-only`, so it drops the `aria-describedby` Radix adds while
    // the tooltip is open; otherwise a screen reader reads the caveat twice (grids review r1 N2).
    <SkyTooltip content={RUN_TIME_HINT} testId={`${testId}-hint`}>
      <span tabIndex={0} aria-describedby={undefined} data-testid={testId}>
        <SkyText size={200} font="numeric">
          {formatDuration(seconds)}
        </SkyText>
        {/* Its own sentence after the value. Round 6 walk W27: it began ". The whole run",
            and a screen reader spoke the leading full stop. */}
        <span className="sr-only"> {RUN_TIME_HINT}.</span>
      </span>
    </SkyTooltip>
  );
}

/** The two columns, in the order the list shows them (after Last run). */
export function connectionRunColumns(): SkyDataGridColumn<DataflowSummary>[] {
  return [
    {
      key: "lastRunRows",
      header: "Rows",
      width: "13%",
      align: "right",
      renderCell: (_value, row) => <ConnectionRowsCell dataflow={row} />,
    },
    {
      key: "lastRunDuration",
      header: "Run time",
      width: "12%",
      align: "right",
      renderCell: (_value, row) => <ConnectionRunTimeCell dataflow={row} />,
    },
  ];
}
